/* ============================================================================
   header.js — Verhalten des Headers: Mobilnavigation, Megamenue (dritte
   Ebene, Tastatur), Suche, Sprach- und Landauswahl, Ausblenden beim Scrollen
   ----------------------------------------------------------------------------
   Geladen von sections/header.liquid als <script type="module">. Stand bis M3
   zusammen mit der Ankuendigungsleiste (heute assets/announcement.js) und der
   Sofortsuche (heute assets/predictive-search.js) in nko-chrome.js.

   Ersetzt aus assets/app.js:
     * initFixedHeader()            (Zeile 83)  — jetzt reines position:sticky
     * .js-toggle-mobile-nav        (Zeile 995)
     * .js-search-open/-close       (Zeile 1217/1222)
     * popupApp fuer data-popup="language" (Zeile 273) — jetzt natives <dialog>
     * die Footer-Akkordeons ueber .js-toggle — jetzt <details>, ganz ohne JS

   Was hier NICHT mehr auftaucht, ist Absicht:

   - KEIN ungedrosselter Scroll-Listener. app.js:83 hat bei jedem
     Scrollframe `js-fixed-header`, `js-scroll-top` und `js-scroll-down` auf
     <body> geschrieben. `position: sticky` haelt den Kopf ohne
     Hauptthread-Arbeit. Unter 1200px hing an dem Listener auch das
     AUSBLENDEN des ganzen Kopfes beim Runterscrollen (style.css:5303) — das
     ist mit einem kleinen, gedrosselten Hoerer zurueck (unten, "Header unter
     1200px"). Das Einklappen der Ankuendigung ab 1200px bleibt weg.
     NOTE(D4): sichtbare Aenderung, im Ledger vermerkt.

   - KEINE Touch-Erkennung. Legacy schaltete das Megamenue ueber
     `.is-touchdevice` / `.is-no-touchdevice`. Auf einem schmalen Fenster OHNE
     Touch war das Untermenue dadurch gar nicht erreichbar. Hier entscheidet
     die Viewportbreite, passend zum CSS.

   - KEIN Warenkorb-Polling. Der Zaehler wird serverseitig gerendert und von
     der Schublade (assets/cart-drawer.js) nachgezogen.

   Die Hoerer der Elemente haengen an EINEM AbortController je Element und
   fallen in disconnectedCallback: der Theme-Editor rendert die Section bei
   jeder Einstellungsaenderung neu, und alte Hoerer am document liefen sonst
   doppelt weiter.
   ==========================================================================  */

const DESKTOP = "(min-width: 1200px)";

/* ----------------------------------------------------------------------------
   Megamenue — dritte Ebene
   ----------------------------------------------------------------------------
   Ersetzt assets/app.js:962-993.

   Die linke Spalte (`.subnav__item[data-value]`) und das Warenpanel
   (`.subnav__goods[data-goods]`) liegen in verschiedenen Teilbaeumen — das
   Panel ist ein Geschwister von `.subnav__inner`, nicht dessen Kind. Mit
   `:hover` und Geschwisterkombinatoren allein ist es deshalb nicht erreichbar;
   ohne diese paar Zeilen bliebe fuer immer das serverseitig vorgewaehlte Panel
   stehen.

   ABWEICHUNGEN VOM LEGACY

   1. NUR IM EIGENEN DROPDOWN. app.js:966-972 raeumte zwar auf das `.nav__dd`
      beschraenkt auf, setzte `is-open` dann aber DOKUMENTWEIT. Ein Handle, das
      in zwei Menues vorkommt, oeffnete dort in beiden gleichzeitig.

   2. AUCH PER TASTATUR. Das Legacy haengte nur an `mouseenter`. Wer tabbte,
      sah zwar die linke Spalte, aber rechts nie das passende Panel. `focusin`
      blubbert, `pointerenter` nicht — deshalb `pointerover` mit `closest`.

   3. AN DER MEDIA QUERY, NICHT AN `.is-touchdevice`. Das Legacy unterschied
      Touch von Nicht-Touch (app.js:962 gegen :976). Ein schmalgezogenes
      Desktop-Fenster galt damit als Nicht-Touch und kam gar nicht an die
      dritte Ebene — als Defekt in docs/chrome-contract.md vermerkt.
   ---------------------------------------------------------------------------- */

function panelUmschalten(item) {
  if (item.classList.contains("is-open")) return;

  const dd = item.closest(".nav__dd");
  const wert = item.dataset.value;
  if (!dd || !wert) return;

  for (const el of dd.querySelectorAll(".subnav__goods.is-open, .subnav__item.is-open")) {
    el.classList.remove("is-open");
  }
  item.classList.add("is-open");
  dd.querySelector(`.subnav__goods[data-goods="${CSS.escape(wert)}"]`)?.classList.add("is-open");
}

/* ----------------------------------------------------------------------------
   Das Megamenue steht direkt im Markup (Entscheidung D-MEGAMENU, Begruendung
   in snippets/nko-header-nav.liquid).

   DELEGIERT AM DOCUMENT, NICHT AN `.nav__menu` (P2-12). Die Hoerer hingen an
   dem Element, das beim Laden da war. Rendert der Theme-Editor die
   Header-Section neu (bei jeder Einstellungsaenderung), steht dort ein neues
   `.nav__menu` ohne Hoerer, und die dritte Ebene war bis zum Neuladen tot.
   Am document ueberleben sie jeden Section-Tausch; `closest` beschraenkt sie
   auf den Header. Ein Modul laeuft je URL nur einmal — die Hoerer haengen
   also genau einmal, auch wenn der Editor das Skript-Tag neu einsetzt.
   -------------------------------------------------------------------------- */
const beiZeiger = (e) => {
  if (!matchMedia(DESKTOP).matches) return;
  const item = e.target.closest?.(".header .subnav__item[data-value]");
  if (item) panelUmschalten(item);
};

document.addEventListener("pointerover", beiZeiger, { passive: true });
document.addEventListener("focusin", beiZeiger);

/* ----------------------------------------------------------------------------
   Megamenue per Tastatur (M12, E6-03) — nur ab 1200px
   ----------------------------------------------------------------------------
   Bis M12 oeffnete das Dropdown per :focus-within schon beim Durchtabben, und
   Tab lief durch jeden seiner Links (28 Tabstopps bis „NAKED Heroes“).
   Escape schloss nichts. Jetzt ist es ein Aufklapper am Hauptlink:

   - Tab laeuft ueber die Hauptlinks, die Dropdowns bleiben zu
     (`visibility: hidden`, ihre Links sind damit kein Tabstopp).
   - Leertaste schaltet `aria-expanded` um, Pfeil runter klappt auf und setzt
     den Fokus auf den ersten Link darin. Enter folgt wie bisher dem Link.
   - Verlaesst der Fokus den Eintrag, klappt er zu.
   - Escape schliesst das offene Dropdown, auch ein per Zeiger geoeffnetes
     (`data-zu`, bis der Zeiger den Eintrag verlaesst), und gibt den Fokus an
     den Hauptlink zurueck, wenn er darin stand.

   Den Zeiger aendert das nicht: der oeffnet weiter per :hover (header.css).
   Delegiert am document wie beiZeiger, aus demselben Grund (P2-12).
   -------------------------------------------------------------------------- */
const hauptlink = (item) => item?.querySelector(":scope > .nav__link");

function aufklappen(item, auf) {
  hauptlink(item)?.setAttribute("aria-expanded", String(auf));
}

document.addEventListener("keydown", (e) => {
  if (!matchMedia(DESKTOP).matches) return;
  const item = e.target.closest?.(".header .nav__item.is-dd");

  if (e.key === "Escape") {
    // der Eintrag mit dem Fokus und der unter dem Zeiger (koennen zwei sein)
    for (const offen of new Set([item, document.querySelector(".header .nav__item.is-dd:hover")])) {
      if (!offen) continue;
      aufklappen(offen, false);
      if (offen.matches(":hover") && !("zu" in offen.dataset)) {
        offen.dataset.zu = "";
        offen.addEventListener("pointerleave", () => delete offen.dataset.zu, { once: true });
      }
    }
    if (item?.contains(document.activeElement)) hauptlink(item).focus();
    return;
  }

  if (e.key !== " " && e.key !== "ArrowDown") return;
  if (!item || e.target !== hauptlink(item)) return;
  e.preventDefault();
  const runter = e.key === "ArrowDown";
  aufklappen(item, runter || e.target.getAttribute("aria-expanded") !== "true");
  // Sichtbar wird es sofort (Verzoegerung 0), der Fokus greift also.
  if (runter) item.querySelector(".nav__dd a")?.focus();
});

document.addEventListener("focusout", (e) => {
  if (!matchMedia(DESKTOP).matches) return;
  const item = e.target.closest?.(".header .nav__item.is-dd");
  if (item && !item.contains(e.relatedTarget)) aufklappen(item, false);
});

/* ----------------------------------------------------------------------------
   Mobiles Navigationspanel
   ----------------------------------------------------------------------------
   `body.js-show-menu` bleibt der Schalter — dieselbe Klasse wie im Legacy;
   header.css haengt die Regeln des Panels daran (style.css, das sie frueher
   trug, ist seit M9 geloescht).
   ---------------------------------------------------------------------------- */

class NkoMobileNav extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector("[data-nav-toggle]");
    this.panel = document.querySelector(this.dataset.panel || ".header__col--nav");
    this.menu = this.panel?.querySelector(".nav__menu");
    if (!this.toggle || !this.panel) return;

    this.ac = new AbortController();
    const { signal } = this.ac;

    /* Gebunden, weil show()/close() ihn je nach Zustand an- und abhaengen
       und removeEventListener dieselbe Funktion braucht. */
    this.onPointerDown = this.onPointerDown.bind(this);

    this.toggle.addEventListener("click", (e) => this.onToggle(e), { signal });
    document.addEventListener("keydown", (e) => this.onKeydown(e), { signal });
    this.panel.addEventListener("click", (e) => this.onPanelClick(e), { signal });

    // Wechselt der Viewport auf Desktop, muss der Mobilzustand weg — sonst
    // bleibt `overflow: hidden` am <body> haengen und die Seite scrollt nicht.
    // Dort ist `aria-expanded` der Tastatur-Zustand des Megamenues (seit M12,
    // oben „Megamenue per Tastatur“): alle Eintraege starten zugeklappt.
    this.mq = matchMedia(DESKTOP);
    this.mq.addEventListener(
      "change",
      (e) => {
        if (!e.matches) return;
        this.close();
        this.ddLinks().forEach((l) => l.setAttribute("aria-expanded", "false"));
      },
      { signal }
    );

    // Zurueck aus dem bfcache stuende das Menue sonst noch offen da.
    window.addEventListener("pagehide", () => this.open && this.close(), { signal });
  }

  /* Der Abbruch nimmt auch die beiden Hoerer mit, die show() nur fuer die
     offene Zeit anhaengt — sie tragen dasselbe Signal. */
  disconnectedCallback() {
    this.ac?.abort();
  }

  get open() {
    return document.body.classList.contains("js-show-menu");
  }

  ddLinks() {
    return this.panel?.querySelectorAll(".nav__item.is-dd > .nav__link") || [];
  }

  onToggle(e) {
    e.preventDefault();
    this.open ? this.close() : this.show();
  }

  show() {
    document.body.classList.add("js-show-menu");
    this.toggle.setAttribute("aria-expanded", "true");
    document.addEventListener("pointerdown", this.onPointerDown, { signal: this.ac.signal });
    /* Dazu `click` in der Capture-Phase, fuer die Tastatur: Enter auf Lupe
       oder Warenkorb loest kein pointerdown aus. Ohne das oeffnete sich die
       Schublade bei offenem Menue in einem inerten Teil der Seite. Capture,
       damit das Menue zu ist, bevor Such- und Schubladen-Hoerer laufen. */
    document.addEventListener("click", this.onPointerDown, { capture: true, signal: this.ac.signal });
    /* Im Panel ist der Eintrag ein echter Aufklapper. `aria-expanded="false"`
       steht seit M12 schon im Markup (ab 1200px der Tastatur-Zustand des
       Megamenues); hier wird ein vom Desktop uebrig gebliebenes "true"
       zurueckgesetzt. */
    this.ddLinks().forEach((l) => l.setAttribute("aria-expanded", "false"));
    this.sperren(true);
  }

  close() {
    document.body.classList.remove("js-show-menu");
    this.toggle.setAttribute("aria-expanded", "false");
    document.removeEventListener("pointerdown", this.onPointerDown);
    document.removeEventListener("click", this.onPointerDown, true);
    this.resetLevels();
    this.sperren(false);
  }

  /* DER REST DER SEITE WIRD INERT, SOLANGE DAS MENUE OFFEN IST (P2-10).
     Vorher wanderte Tab aus dem offenen Menue in den abgedunkelten
     Seiteninhalt. `inert` statt einer eigenen Tab-Falle: es nimmt die Knoten
     auch aus dem Accessibility-Baum und kostet keine Zeile Fokuslogik.

     Gesperrt werden die Ankuendigung, die Logo-Spalte und jedes Kind von
     <body> ausser dem Header. <nko-locale> bleibt frei — der Sprachdialog
     wird AUS dem Menue geoeffnet und waere sonst selbst inert.

     NICHT GESPERRT: DIE WERKZEUGSPALTE UND DIE SUCHSPALTE. Lupe und
     Warenkorb stehen bei offenem Menue sichtbar darueber und sind im Laden
     bedienbar. Inert traf ein Tipp darauf <body>: das Menue ging zu, aber
     weder der Link noch der Hoerer der Schublade bekam den Klick. Die
     Suchspalte ist bis zum Oeffnen `display: none`, braucht also keine
     Sperre — mit ihr konnte die Lupe das Suchfeld nicht fokussieren.

     Ein Klick auf eine inerte Flaeche trifft den naechsten nicht-inerten
     Vorfahren (<body>); onPointerDown schliesst das Menue deshalb weiterhin. */
  sperren(an) {
    if (!an) {
      (this.gesperrt || []).forEach((el) => (el.inert = false));
      this.gesperrt = null;
      return;
    }
    const kopf = this.closest(".header");
    const ziele = [
      ...document.querySelectorAll("body > :not(.header):not(script):not(style):not(link)"),
      ...(kopf ? kopf.querySelectorAll(".shop__msg, .header__col--logo") : []),
    ].filter((el) => !el.inert);
    ziele.forEach((el) => (el.inert = true));
    this.gesperrt = ziele;
  }

  resetLevels() {
    this.menu?.removeAttribute("data-level");
    this.panel
      ?.querySelectorAll(".nav__item[data-open]")
      .forEach((el) => el.removeAttribute("data-open"));
    this.panel
      ?.querySelectorAll('.nav__link[aria-expanded="true"]')
      .forEach((el) => el.setAttribute("aria-expanded", "false"));
  }

  /* Fokus zurueck auf den Eintrag, ueber den man in die dritte Ebene kam.
     Ohne das blieb er auf dem jetzt versteckten Zurueck-Knopf haengen. */
  zurueckAufEbene2() {
    this.menu?.setAttribute("data-level", "2");
    this.letzterEintrag?.querySelector(".subnav__link")?.focus();
  }

  /* Von der Kategorieliste zurueck auf die erste Ebene. Der Fokus geht auf
     den Hauptpunkt, aus dem man kam — der Zurueck-Knopf ist danach
     `display: none`. */
  zurueckAufEbene1() {
    const link = this.panel?.querySelector(".nav__item[data-open] > .nav__link");
    this.resetLevels();
    link?.focus();
  }

  onKeydown(e) {
    if (e.key !== "Escape" || !this.open) return;

    /* Ein offener modaler Dialog (Sprachwahl aus dem Menue) behandelt sein
       Escape selbst. Hoerten beide, schloss Escape Dialog UND Menue, und der
       Fokus fiel auf <body>, weil der Ausloeser im geschlossenen Panel lag. */
    if (document.querySelector("dialog[open]")) return;

    // Steht das Panel auf Ebene 3, geht Escape erst eine Ebene zurueck.
    if (this.menu?.getAttribute("data-level") === "3") {
      this.zurueckAufEbene2();
      return;
    }
    this.close();
    this.toggle.focus();
  }

  onPointerDown(e) {
    if (!this.open) return;
    if (this.panel.contains(e.target) || this.contains(e.target)) return;
    /* Der Sprachdialog wird aus dem Menue geoeffnet, liegt aber ausserhalb
       des Panels: jeder Tipp darin — auch sein X — schloss das Menue mit, und
       der Fokus fiel beim Schliessen auf <body>, weil der Ausloeser im
       geschlossenen Panel lag. Wie bei Escape (onKeydown) bleibt das Menue
       unter einem offenen Dialog stehen. */
    if (e.target.closest?.("dialog[open]")) return;
    this.close();
  }

  /* Unterhalb von 1200px oeffnet der Klick auf einen Elternpunkt dessen
     Untermenue, statt der Verlinkung zu folgen — wie im Legacy auf
     Touchgeraeten (app.js:949). Oberhalb uebernimmt reines CSS (:hover /
     :focus-within), hier passiert dann nichts. */
  onPanelClick(e) {
    if (this.mq.matches) return;

    /* Dritte Ebene: ein Tippen auf eine Kategorie oeffnet ihr Warenpanel,
       statt der Verlinkung zu folgen — wie im Legacy (app.js:976-989). */
    const eintrag = e.target.closest(".subnav__item[data-value]");
    if (eintrag && this.panel.contains(eintrag)) {
      e.preventDefault();
      panelUmschalten(eintrag);
      this.menu?.setAttribute("data-level", "3");
      this.letzterEintrag = eintrag;
      /* IM EIGENEN DROPDOWN suchen, nicht panelweit (P2-10). Jedes Dropdown
         hat ein serverseitig vorgewaehltes `is-open`-Panel; panelweit traf
         die Suche bei Helme und Bekleidung den versteckten Knopf von Brillen,
         und der Fokus fiel auf <body>. */
      eintrag.closest(".nav__dd")?.querySelector(".subnav__goods.is-open .js-back")?.focus();
      return;
    }

    /* Der Weg zurueck. Im Legacy gab es ihn zwar im Markup, der Handler
       (app.js:991) hing aber an `.is-touchdevice` — in einem schmalgezogenen
       Desktop-Fenster war die dritte Ebene damit eine Sackgasse. Die zweite
       Ebene hatte gar keinen: dort steht jetzt derselbe Knopf am Kopf der
       Kategorieliste (snippets/nko-header-nav.liquid). Der im Warenpanel
       fuehrt auf Ebene 2, der andere auf die erste. */
    const zurueck = e.target.closest(".js-back");
    if (zurueck && this.panel.contains(zurueck)) {
      e.preventDefault();
      if (zurueck.closest(".subnav__goods")) this.zurueckAufEbene2();
      else this.zurueckAufEbene1();
      return;
    }

    const link = e.target.closest(".nav__item.is-dd > .nav__link");
    if (!link || !this.panel.contains(link)) return;

    e.preventDefault();
    const item = link.parentElement;
    const isOpen = item.hasAttribute("data-open");

    this.resetLevels();
    if (!isOpen) {
      item.setAttribute("data-open", "");
      link.setAttribute("aria-expanded", "true");
      this.menu?.setAttribute("data-level", "2");
      /* Auf Ebene 2 ist der eigene Link ausgeblendet (Drill-down). Behielte er
         den Fokus, laege der auf einem unsichtbaren Element. */
      item.querySelector(".nav__dd .subnav__link")?.focus();
    }
  }
}

/* ----------------------------------------------------------------------------
   Header unter 1200px beim Runterscrollen ausblenden (Entscheidung
   D-HEADER-HIDE, Paritaet)
   ----------------------------------------------------------------------------
   Das Legacy blendete den Header unter 1200px aus, sobald man ueber 200px
   hinaus nach unten scrollte (`.js-fixed-header.js-scroll-down .header
   { display: none }`, style.css:5303), und zeigte ihn beim Hochscrollen ohne
   Ankuendigungsleiste wieder. Beim Port war der Scroll-Listener aus app.js:83
   ersatzlos gefallen — mobil belegte der Kopf dauerhaft 89px, gut 10 Prozent
   eines Handybildschirms.

   Die Scrollrichtung kann CSS nicht erkennen, deshalb bleibt ein kleiner
   Hoerer — aber anders als in app.js:
     * passiv und per requestAnimationFrame gedrosselt, statt bei jedem
       Scrollframe drei Klassen auf <body> zu schreiben;
     * er schreibt NUR bei einem Richtungswechsel, als `data-scroll` an <html>;
     * ab 1200px tut er nichts (dort bleibt der Kopf, chrome-contract §5).
   Ausgeblendet wird per `translate` (header.css §5), nicht per
   `display: none`: das Legacy verschob damit bei jedem Wechsel die ganze
   Seite um die Kopfhoehe. Auf Seiten mit klebender Sprungleiste (Produkt-
   seite) faehrt beim Runterscrollen nur die Ankuendigung weg; das
   entscheidet allein das CSS dort, dieser Hoerer weiss davon nichts.

   Auf Modulebene, nicht an einem Element: er gilt der ganzen Seite, und ein
   Modul laeuft je URL nur einmal.
   -------------------------------------------------------------------------- */
{
  const html = document.documentElement;
  const breit = matchMedia(DESKTOP);
  let letzteY = scrollY;
  let geplant = false;

  const setzen = (wert) => {
    if (html.dataset.scroll === wert) return;
    if (wert) html.dataset.scroll = wert;
    else delete html.dataset.scroll;
  };

  const pruefen = () => {
    geplant = false;
    const y = scrollY;
    // Bei offenem Menue oder Suche bleibt der Kopf stehen.
    const cls = document.body.classList;
    if (breit.matches || cls.contains("js-show-menu") || cls.contains("js-show-search")) {
      letzteY = y;
      return setzen(null);
    }
    if (y <= 200) setzen(null);
    else if (Math.abs(y - letzteY) > 4) setzen(y > letzteY ? "down" : "up");
    letzteY = y;
  };

  addEventListener(
    "scroll",
    () => {
      if (geplant) return;
      geplant = true;
      requestAnimationFrame(pruefen);
    },
    { passive: true }
  );
  breit.addEventListener("change", pruefen);
}

/* ----------------------------------------------------------------------------
   Suche
   ----------------------------------------------------------------------------
   Aufklappen, Schliessen, Aussenklick und Escape stehen hier. Die Sofortsuche
   (Vorschlaege beim Tippen, fetch auf /search/suggest.json) liegt in
   assets/predictive-search.js und kommt erst beim ersten Oeffnen bzw. Fokus
   der Suche per import() — wer nie sucht, laedt sie nie. Die Adresse steht
   versioniert in `data-predictive-url` (sections/header.liquid); das
   Attribut gibt es nur, wenn der Haendler die Vorschlaege eingeschaltet hat
   (`settings.predictive_search_enabled`). Vorher las nko-chrome.js dafuer
   `window.theme.settings.nkoPredictiveSearch`.
   ---------------------------------------------------------------------------- */

class NkoSearch extends HTMLElement {
  connectedCallback() {
    this.input = document.querySelector("#HeaderSearchForm input[name='q']");
    this.openBtn = document.querySelector("[data-search-open]");
    this.closeBtn = document.querySelector("[data-search-close]");
    if (!this.openBtn) return;

    this.ac = new AbortController();
    const { signal } = this.ac;

    // Gebunden: show() und hide() haengen ihn an und ab.
    this.onPointerDown = this.onPointerDown.bind(this);

    this.openBtn.addEventListener("click", () => this.show(), { signal });
    this.closeBtn?.addEventListener("click", () => this.hide(), { signal });
    document.addEventListener("keydown", (e) => this.onKeydown(e), { signal });

    // Zurueck aus dem bfcache stuende die Suche sonst noch offen da.
    window.addEventListener("pagehide", () => this.open && this.hide({ fokusZurueck: false }), {
      signal,
    });

    // Fokus im Suchfeld laedt die Sofortsuche ebenfalls (etwa per Tastatur,
    // wenn die Suche schon offen steht).
    this.addEventListener("focusin", () => this.vorschlaegeLaden(), { signal });
  }

  disconnectedCallback() {
    this.ac?.abort();
  }

  /* Holt assets/predictive-search.js genau einmal und haengt es an das
     Suchfeld. Was getippt wird, bevor das Modul da ist, fragt es nach dem
     Eintreffen nach — sonst ginge der erste Buchstabe verloren. Ein
     vorbelegtes Feld (auf /search steht das Suchwort drin) loest dagegen
     nichts aus, wie bisher: Vorschlaege gibt es erst auf eine Eingabe. */
  vorschlaegeLaden() {
    const url = this.dataset.predictiveUrl;
    const ergebnisse = document.querySelector("#PredictiveResults");
    if (this.vorschlaege || !url || !ergebnisse || !this.input) return;

    const { signal } = this.ac;
    let getippt = false;
    this.input.addEventListener("input", () => (getippt = true), { signal, once: true });

    this.vorschlaege = import(url)
      .then(({ vorschlaegeVerdrahten }) => {
        if (signal.aborted) return;
        const sofortsuche = vorschlaegeVerdrahten({ input: this.input, ergebnisse, signal });
        if (getippt) sofortsuche.beiEingabe();
      })
      .catch(() => {
        // Netzfehler: beim naechsten Oeffnen noch einmal versuchen.
        this.vorschlaege = null;
      });
  }

  get open() {
    return document.body.classList.contains("js-show-search");
  }

  show() {
    document.body.classList.add("js-show-search");
    this.openBtn.setAttribute("aria-expanded", "true");
    this.vorschlaegeLaden();
    /* SYNCHRON, NOCH IM KLICK. iOS blendet die Tastatur nur ein, wenn focus()
       innerhalb der Beruehrung laeuft. Hier stand requestAnimationFrame: das
       Feld bekam einen Frame spaeter zwar Rahmen und Einfuegemarke, aber keine
       Tastatur, und Getipptes kam nicht an — erst ein zweiter Tipp ins Feld
       half. Warten muss man nicht: die Spalte ist mit der Klasse oben schon
       `display: block`, und focus() rechnet den Stil selbst nach, bevor es
       prueft (so fokussierte auch das Legacy, app.js:1219). */
    this.input?.focus();
    document.addEventListener("pointerdown", this.onPointerDown, { signal: this.ac.signal });
  }

  /* `fokusZurueck` ist nur beim ABSICHTLICHEN Schliessen richtig — Escape oder
     der Schliessen-Knopf. Wer daneben klickt, will woandershin; ihm den Fokus
     auf den Lupenknopf zu reissen, waere ein Sprung mitten in der Bewegung. */
  hide({ fokusZurueck = true } = {}) {
    document.body.classList.remove("js-show-search", "js-search-show-suggested");
    this.openBtn.setAttribute("aria-expanded", "false");
    document.removeEventListener("pointerdown", this.onPointerDown);
    if (fokusZurueck) this.openBtn.focus();
  }

  /* Ersetzt den Aussenklick aus assets/app.js:1206-1214. Dort haing er an
     `.js-clear-handlers` — der abgedunkelten Ebene — und raeumte in einem Zug
     Suche, Filter, Menue und Popups ab. Hier reicht der Such-Zweig: das
     Mobilmenue faengt seinen Aussenklick selbst ab (NkoMobileNav), und die
     Popups haben ihre eigene Blende.

     Gehoert wird nicht die Ebene, sondern alles ausserhalb des Panels — dann
     schliesst auch ein Klick auf Seiteninhalt, den die Ebene nicht abdeckt. */
  onPointerDown(e) {
    if (!this.open) return;
    if (this.contains(e.target) || this.openBtn.contains(e.target)) return;
    this.hide({ fokusZurueck: false });
  }

  onKeydown(e) {
    if (e.key === "Escape" && this.open) this.hide();
  }
}

/* ----------------------------------------------------------------------------
   Sprach- und Landauswahl
   ----------------------------------------------------------------------------
   Legacy oeffnete dafuer ein `.popup--language` ueber popupApp und rechnete
   dessen Position per jQuery aus. Ein modales <dialog> bringt Top-Layer,
   Fokusfalle, Escape und Backdrop mit — dieselbe Loesung wie die Lightbox auf
   der Produktseite.
   ---------------------------------------------------------------------------- */

class NkoLocale extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector("dialog");
    this.form = this.querySelector("form");
    if (!this.dialog) return;

    this.ac = new AbortController();
    const { signal } = this.ac;

    // DOKUMENTWEIT suchen, nicht in `this`. Die Ausloeser stehen in der
    // Werkzeugleiste und im Navigationspanel — beide AUSSERHALB von
    // <nko-locale>, das nur Dialog und Formular umschliesst. Mit `this.`
    // fand der Konstruktor keinen einzigen Knopf und der Dialog liess sich
    // nicht oeffnen.
    document.querySelectorAll("[data-locale-open]").forEach((btn) =>
      btn.addEventListener(
        "click",
        () => {
          this.opener = btn;
          this.dialog.showModal();
        },
        { signal }
      )
    );

    this.querySelector("[data-locale-close]")?.addEventListener("click", () => this.dialog.close(), {
      signal,
    });

    // Klick auf den Backdrop schliesst. Das <dialog> selbst fuellt den
    // Viewport, das Panel liegt darin — trifft der Klick nicht das Panel,
    // war es der Hintergrund.
    this.dialog.addEventListener(
      "click",
      (e) => {
        if (!e.target.closest(".locale__panel")) this.dialog.close();
      },
      { signal }
    );

    // Geschlossen ohne Uebernehmen verwirft die Auswahl. Sonst zeigte der
    // Dialog beim naechsten Oeffnen ein Land, das gar nicht aktiv ist.
    // `reset()` stellt die serverseitig gesetzten `selected` wieder her.
    this.dialog.addEventListener(
      "close",
      () => {
        this.form?.reset();
        this.syncFlag();
        this.opener?.focus();
      },
      { signal }
    );

    // Zurueck-Taste nach dem Wechsel: der bfcache stellt die alte Seite MIT
    // offenem Dialog und der neuen Auswahl wieder her.
    window.addEventListener(
      "pageshow",
      (e) => {
        if (e.persisted && this.dialog.open) this.dialog.close();
      },
      { signal }
    );

    // Die Flagge neben dem Land-Select. Ihre Adresse steht an jeder <option>
    // (`data-flag`, Shopify-CDN, sections/header.liquid) — nicht mehr von
    // flagcdn.com zusammengesetzt (P2-13). Fehlt sie, bleibt die Flagge weg.
    this.country = this.querySelector('select[name="country_code"]');
    this.flag = this.querySelector(".locale__flag");
    this.country?.addEventListener("change", () => this.syncFlag(), { signal });
  }

  disconnectedCallback() {
    this.ac?.abort();
  }

  syncFlag() {
    if (!this.country || !this.flag) return;
    const src = this.country.selectedOptions[0]?.dataset.flag;
    this.flag.hidden = !src;
    if (!src || this.flag.getAttribute("src") === src) return;
    // `image_tag` schreibt ein srcset; das gewaenne gegen ein neues src.
    this.flag.removeAttribute("srcset");
    this.flag.src = src;
  }
}

/* --------------------------------------------------------------------------- */

for (const [name, klasse] of [
  ["nko-mobile-nav", NkoMobileNav],
  ["nko-search", NkoSearch],
  ["nko-locale", NkoLocale],
]) {
  if (!customElements.get(name)) customElements.define(name, klasse);
}

/* ----------------------------------------------------------------------------
   NUR ALS MODUL LADEN: <script src="…" type="module">, nie mit `defer`.

   Diese Datei wird ungebaut ausgeliefert; ihren eigenen Gueltigkeitsbereich
   gibt ihr der Modulbereich. Als klassisches Skript laegen alle Namen oben
   global und stiessen sich an denen der anderen Theme-Dateien. Module laufen
   wie `defer` nach dem Parsen in Dokumentreihenfolge, aber nur einmal je URL
   und immer im strict mode.
   ---------------------------------------------------------------------------- */
